import { useCallback, useEffect, useRef } from 'react';
import {
  trackBlogArticleCtaClicked,
  trackBlogArticleRelatedClicked,
  trackBlogArticleScroll,
  trackBlogArticleView,
  type BlogArticleCtaType,
  type BlogArticleScrollDepth,
} from '../../analytics';

const SCROLL_DEPTHS: BlogArticleScrollDepth[] = [25, 50, 75, 100];

type UseBlogArticleAnalyticsArgs = {
  slug?: string | null;
  categorySlug?: string | null;
  locale?: string | null;
  enabled?: boolean;
};

/**
 * Tracking article blog Pro : vue + scroll + helpers CTA / related.
 */
export function useBlogArticleAnalytics({
  slug,
  categorySlug = null,
  locale = null,
  enabled = true,
}: UseBlogArticleAnalyticsArgs) {
  const viewedSlugRef = useRef<string | null>(null);
  const firedDepthsRef = useRef<Set<number>>(new Set());

  useEffect(() => {
    if (!enabled || !slug) return;
    if (viewedSlugRef.current === slug) return;
    viewedSlugRef.current = slug;
    firedDepthsRef.current = new Set();
    trackBlogArticleView({ slug, categorySlug, locale });
  }, [enabled, slug, categorySlug, locale]);

  useEffect(() => {
    if (!enabled || !slug || typeof window === 'undefined') return;

    const onScroll = () => {
      const doc = document.documentElement;
      const scrollable = Math.max(doc.scrollHeight - window.innerHeight, 1);
      const pct = Math.min(100, Math.round((window.scrollY / scrollable) * 100));
      for (const depth of SCROLL_DEPTHS) {
        if (pct < depth || firedDepthsRef.current.has(depth)) continue;
        firedDepthsRef.current.add(depth);
        trackBlogArticleScroll({ slug, depth, categorySlug, locale });
      }
    };

    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, [enabled, slug, categorySlug, locale]);

  const trackCta = useCallback(
    (ctaType: BlogArticleCtaType, destination?: string | null) => {
      if (!slug) return;
      trackBlogArticleCtaClicked({
        slug,
        ctaType,
        categorySlug,
        destination: destination ?? null,
        locale,
      });
    },
    [slug, categorySlug, locale]
  );

  const trackRelated = useCallback(
    (targetSlug: string) => {
      if (!slug || !targetSlug) return;
      trackBlogArticleRelatedClicked({
        slug,
        targetSlug,
        categorySlug,
        locale,
      });
    },
    [slug, categorySlug, locale]
  );

  return { trackCta, trackRelated };
}
